import { useSessionTokenGetter } from '@develio/auth/client';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AddFieldButton } from './AddFieldButton';
import { EmbedPanel } from './EmbedPanel';
import { FieldCard } from './FieldCard';
import { FieldEditor } from './FieldEditor';
import { MANUFACTURING_TEMPLATE } from './templates';
import type { FieldType, FormField } from './types';
import { useDraftState } from './useDraftState';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function IntakeFormBuilderRoute() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const getToken = useSessionTokenGetter();

  const formId = id ?? 'new';
  const draftState = useDraftState(formId);

  const [formName, setFormName] = useState('Untitled Form');
  const [formDescription, setFormDescription] = useState('');
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [loading, setLoading] = useState(!!id);
  const [formStatus, setFormStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  const [rightTab, setRightTab] = useState<'properties' | 'share'>('properties');

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── On mount: load existing form OR apply template ──────────────────────
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect — id, searchParams, getToken, and draftState are all stable on mount and re-fetching on every render would cause infinite loops
  useEffect(() => {
    const template = searchParams.get('template');

    if (id) {
      // Editing an existing form — fetch from API
      (async () => {
        try {
          const token = await getToken();
          const res = await fetch(`${API}/api/v1/intake-forms/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const json = await res.json();
            const form = json.form;
            setFormName(form.name);
            setFormDescription(form.description ?? '');
            setFormStatus(form.status as 'draft' | 'published' | 'archived');
            // Prefer localStorage draft if it exists (more recent edits)
            const draft = draftState.load();
            setFields(draft ?? (form.fields as FormField[]));
          }
        } finally {
          setLoading(false);
        }
      })();
    } else {
      // New form — apply template if requested
      if (template === 'manufacturing') {
        setFormName(MANUFACTURING_TEMPLATE.name);
        setFormDescription(MANUFACTURING_TEMPLATE.description);
        setFields(MANUFACTURING_TEMPLATE.fields());
      } else {
        // Check for a saved new-form draft
        const draft = draftState.load();
        if (draft) setFields(draft);
      }
    }
  }, []);

  // ── Auto-save to localStorage on fields change ────────────────────────────
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      draftState.save(fields);
    }, 500);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [fields, draftState]);

  // ── DnD sensors ───────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFields((prev) => {
        const oldIndex = prev.findIndex((f) => f.id === active.id);
        const newIndex = prev.findIndex((f) => f.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  // ── Field mutations ───────────────────────────────────────────────────────
  const handleAddField = (type: FieldType) => {
    const newField: FormField = {
      id: nanoid(),
      type,
      label: `New ${type} field`,
      required: false,
      ...(type === 'select' || type === 'multiselect' ? { options: [] } : {}),
    };
    setFields((prev) => [...prev, newField]);
    setSelectedId(newField.id);
  };

  const handleUpdateField = (updated: FormField) => {
    setFields((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
  };

  const handleDeleteField = (fieldId: string) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    if (selectedId === fieldId) setSelectedId(null);
  };

  // ── Save to API ────────────────────────────────────────────────────────────
  const handleSave = async (status: 'draft' | 'published') => {
    setSaveState('saving');
    try {
      const token = await getToken();
      let savedId = id;

      if (!id) {
        // Create new form
        const res = await fetch(`${API}/api/v1/intake-forms`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formName,
            description: formDescription || null,
            fields,
            status,
            templateSlug: searchParams.get('template') ?? null,
          }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const json = await res.json();
        savedId = json.formId as string;
        draftState.clear();
        setSaveState('saved');
        setFormStatus(status);
        navigate(`/forms/${savedId}/edit`, { replace: true });
      } else {
        // Update existing
        const res = await fetch(`${API}/api/v1/intake-forms/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formName,
            description: formDescription || null,
            fields,
            status,
          }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
        draftState.clear();
        setSaveState('saved');
        setFormStatus(status);
      }
    } catch {
      setSaveState('error');
    }
  };

  const selectedField = fields.find((f) => f.id === selectedId) ?? null;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-0 -m-6">
      {/* ── Header ── */}
      <div className="flex items-center gap-4 border-b border-slate-200 bg-white px-6 py-4">
        <Link to="/forms" className="text-sm text-slate-500 hover:text-slate-900 hover:underline">
          ← Forms
        </Link>
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            className="w-full max-w-sm truncate rounded-md border border-transparent px-2 py-1 text-lg font-semibold text-slate-900 focus:border-slate-300 focus:outline-none focus:bg-white bg-transparent"
            placeholder="Form name"
            aria-label="Form name"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saveState === 'saved' && <span className="text-xs text-green-600">Saved ✓</span>}
          {saveState === 'error' && <span className="text-xs text-red-600">Save failed</span>}
          {id && (
            <Link
              to={`/forms/${id}/preview`}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Preview
            </Link>
          )}
          <button
            type="button"
            onClick={() => handleSave('draft')}
            disabled={saveState === 'saving'}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {saveState === 'saving' ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            onClick={() => handleSave('published')}
            disabled={saveState === 'saving'}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            Publish
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: sortable field list */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
          {fields.length === 0 && (
            <div className="mb-4 rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="text-sm text-slate-500">No fields yet.</p>
              <p className="mt-1 text-xs text-slate-400">Click "Add field" below to get started.</p>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {fields.map((field) => (
                  <FieldCard
                    key={field.id}
                    field={field}
                    isSelected={selectedId === field.id}
                    onSelect={() => setSelectedId(field.id)}
                    onDelete={() => handleDeleteField(field.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="mt-3">
            <AddFieldButton onAdd={handleAddField} />
          </div>

          <p className="mt-3 text-xs text-slate-400 text-center">
            {fields.length} field{fields.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Right: tabbed panel (Properties | Share) */}
        <div className="w-80 shrink-0 flex flex-col border-l border-slate-200 bg-white overflow-hidden">
          {/* Tab bar */}
          <div className="flex shrink-0 border-b border-slate-200">
            <button
              type="button"
              onClick={() => setRightTab('properties')}
              className={`flex-1 py-2.5 text-xs font-medium ${
                rightTab === 'properties'
                  ? 'border-b-2 border-slate-900 text-slate-900'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Properties
            </button>
            {id && (
              <button
                type="button"
                onClick={() => setRightTab('share')}
                className={`flex-1 py-2.5 text-xs font-medium ${
                  rightTab === 'share'
                    ? 'border-b-2 border-slate-900 text-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Share
              </button>
            )}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {rightTab === 'share' && id ? (
              <EmbedPanel formId={id} formStatus={formStatus} />
            ) : selectedField ? (
              <FieldEditor field={selectedField} onChange={handleUpdateField} />
            ) : (
              <div className="flex h-full items-center justify-center p-6">
                <p className="text-center text-sm text-slate-400">
                  Select a field to edit its properties.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
